import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { HTTP_INTERCEPTORS, HttpClientModule, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { IntlModule } from '@progress/kendo-angular-intl';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { GridModule } from '@progress/kendo-angular-grid';
import { LabelModule } from '@progress/kendo-angular-label';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { ConfigModule } from 'lib-config';

import { CustomNotificationService } from './services/custom-notification.service';

import { ApiInterceptor } from './interceptors/api.interceptor';

import { TemplateReferenceComponent } from './components/template-reference/template-reference.component';
import { GridSearchComponent } from './components/grid-search/grid-search.component';
import { CompanyDropdownComponent } from './components/dropdowns/company-dropdown.component';
import { DirectoryUsersDropdownComponent } from './components/dropdowns/directory-users-dropdown.component';
import { UsersDropdownComponent } from './components/dropdowns/users-dropdown.component';
import { ApplicationRolesDropdownComponent } from './components/dropdowns/application-roles-dropdown.component';
import { CompanyRolesDropdownComponent } from './components/dropdowns/company-roles-dropdown.component';
import { GridStringFilterComponent } from './components/grid-string-filter/grid-string-filter.component';
import { GridDateFilterComponent } from './components/grid-date-filter/grid-date-filter.component';
import { JurisdictionObligationOptionsDropdownComponent } from './components/dropdowns/jurisdiction-obligation-options-dropdown.component';
import { GridCompanyFilterComponent } from './components/grid-company-filter/grid-company-filter.component';
import { LegalEntityDropdownComponent } from './components/dropdowns/legal-entity-dropdown.component';
import { GridLegalEntityFilterComponent } from './components/grid-legal-entity-filter/grid-legal-entity-filter.component';
import { SimpleUsersDropdownComponent } from './components/dropdowns/simple-users-dropdown.component';
import { GridUploadedByFilterComponent } from './components/grid-uploaded-by-filter/grid-uploaded-by-filter.component';
import { GridJurisdictionObligationFilterComponent } from './components/grid-jurisdiction-obligation-filter/grid-jurisdiction-obligation-filter.component';
import { JurisdictionObligationDropdownComponent } from './components/dropdowns/jurisdiction-obligation-dropdown.component';
import { ReturnTypeDropdownComponent } from './components/dropdowns/return-type-dropdown.component';
import { GridReturnTypeFilterComponent } from './components/grid-return-type-filter/grid-return-type-filter.component';
import { GridBooleanFilterComponent } from './components/grid-boolean-filter/grid-boolean-filter.component';
import { BooleanDropdownComponent } from './components/dropdowns/boolean-dropdown.component';
import { GridEfileValidationStatusFilterComponent } from './components/grid-efile-validation-status-filter/grid-efile-validation-status-filter.component';
import { EFileValidationStatusDropdownComponent } from './components/dropdowns/efile-validation-status-dropdown.component';
import { FileUploadComponent } from './components/file-upload/file-upload.component';
import { DragAndDropDirective } from './directives/drag-and-drop.directive';
import { GridEfileSubmissionStatusFilterComponent } from './components/grid-efile-submission-status-filter/grid-efile-submission-status-filter.component';
import { EFileSubmissionStatusDropdownComponent } from './components/dropdowns/efile-submission-status-dropdown.component';
import { ReturnTypeByCredentialTypeDropdownComponent } from './components/dropdowns/return-type-by-credential-type-dropdown.component';
import { DatepickerNoTimeComponent } from './components/datepicker-no-time/datepicker-no-time.component';
import { ToastrModule } from 'ngx-toastr';
import { LoadingOverlayComponent } from './components/loading-overlay/loading-overlay.component';
import { DateInputResetButtonDirective } from './directives/date-picker-reset-button.directive';
import { JurisdictionObligationByCompanyDropdownComponent } from './components/dropdowns/jurisdiction-obligation-by-company-dropdown.component';
import { InputErrorDisplayComponent } from './components/input-error-display/input-error-display.component';

@NgModule({ declarations: [
        TemplateReferenceComponent,
        GridSearchComponent,
        CompanyDropdownComponent,
        LegalEntityDropdownComponent,
        SimpleUsersDropdownComponent,
        JurisdictionObligationDropdownComponent,
        JurisdictionObligationByCompanyDropdownComponent,
        ReturnTypeDropdownComponent,
        ReturnTypeByCredentialTypeDropdownComponent,
        BooleanDropdownComponent,
        EFileValidationStatusDropdownComponent,
        EFileSubmissionStatusDropdownComponent,
        JurisdictionObligationOptionsDropdownComponent,
        DirectoryUsersDropdownComponent,
        UsersDropdownComponent,
        ApplicationRolesDropdownComponent,
        CompanyRolesDropdownComponent,
        GridStringFilterComponent,
        GridDateFilterComponent,
        GridCompanyFilterComponent,
        GridLegalEntityFilterComponent,
        GridUploadedByFilterComponent,
        GridJurisdictionObligationFilterComponent,
        GridReturnTypeFilterComponent,
        GridBooleanFilterComponent,
        GridEfileValidationStatusFilterComponent,
        GridEfileSubmissionStatusFilterComponent,
        FileUploadComponent,
        DragAndDropDirective,
        DateInputResetButtonDirective,
        DatepickerNoTimeComponent,
        LoadingOverlayComponent,
        InputErrorDisplayComponent
    ],
    exports: [
        HttpClientModule,
        FormsModule,
        IntlModule,
        DropDownsModule,
        LabelModule,
        TooltipModule,
        GridModule,
        DateInputsModule,
        TemplateReferenceComponent,
        GridSearchComponent,
        CompanyDropdownComponent,
        LegalEntityDropdownComponent,
        SimpleUsersDropdownComponent,
        JurisdictionObligationDropdownComponent,
        JurisdictionObligationByCompanyDropdownComponent,
        ReturnTypeDropdownComponent,
        ReturnTypeByCredentialTypeDropdownComponent,
        BooleanDropdownComponent,
        EFileValidationStatusDropdownComponent,
        EFileSubmissionStatusDropdownComponent,
        JurisdictionObligationOptionsDropdownComponent,
        DirectoryUsersDropdownComponent,
        UsersDropdownComponent,
        ApplicationRolesDropdownComponent,
        CompanyRolesDropdownComponent,
        GridStringFilterComponent,
        GridDateFilterComponent,
        GridCompanyFilterComponent,
        GridLegalEntityFilterComponent,
        GridUploadedByFilterComponent,
        GridJurisdictionObligationFilterComponent,
        GridReturnTypeFilterComponent,
        GridBooleanFilterComponent,
        GridEfileValidationStatusFilterComponent,
        GridEfileSubmissionStatusFilterComponent,
        FileUploadComponent,
        DragAndDropDirective,
        DateInputResetButtonDirective,
        DatepickerNoTimeComponent,
        LoadingOverlayComponent
    ], imports: [CommonModule,
        FormsModule,
        ReactiveFormsModule,
        IntlModule,
        DropDownsModule,
        LabelModule,
        TooltipModule,
        GridModule,
        DateInputsModule,
        ConfigModule,
        ToastrModule.forRoot({
            preventDuplicates: true,
            resetTimeoutOnDuplicate: true,
            enableHtml: true,
            positionClass: 'toast-top-center'
        })], providers: [
        CustomNotificationService,
        {
            provide: HTTP_INTERCEPTORS,
            useClass: ApiInterceptor,
            multi: true
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class UtilModule {

}
